import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/events/event-soir.png";

const feteDuCitronMenton: ChauffeurPage = {
  slug: "fete-du-citron-menton",
  title: "Chauffeur privé Fête du Citron Menton – VTC",
  subtitle:
    "Chauffeur privé pendant la Fête du Citron à Menton : accès aux jardins Biovès et aux corsos, transferts depuis l'aéroport de Nice et Monaco.",
  keywords: [
    "chauffeur fête du citron",
    "vtc fête du citron menton",
    "chauffeur privé menton février",
  ],
  hero: {
    title: "Chauffeur privé pendant la Fête du Citron à Menton",
    subtitle:
      "Jardins Biovès et corsos sur le front de mer : un chauffeur dédié quand Menton se remplit en février et que les rues se ferment pour les défilés.",
    keywords: [
      "chauffeur Fête du Citron",
      "VTC jardins Biovès",
      "transfert aéroport Nice Menton",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Chauffeur privé pendant la Fête du Citron à Menton",
    badges: ["Transfert aéroport", "Accès corsos", "Depuis Monaco"],
  },
  benefits: {
    heading: "Pourquoi un chauffeur pendant la Fête du Citron",
    items: [
      {
        title: "Corsos et rues fermées maîtrisés",
        description:
          "Les défilés ferment le front de mer et le centre de Menton. Le chauffeur connaît les déposes autorisées au plus près des jardins Biovès.",
      },
      {
        title: "Aéroport de Nice & Monaco",
        description:
          "Nice Côte d'Azur est à une trentaine de kilomètres, Monaco à dix minutes. Accueil pancarte, suivi de vol et transfert direct.",
      },
      {
        title: "Événement d'hiver confortable",
        description:
          "En février, un chauffeur vous évite l'attente dehors et les correspondances : trajet au chaud entre l'hôtel, les jardins et les corsos.",
      },
      {
        title: "5,0/5 sur 38 avis Google",
        description:
          "Ponctualité, discrétion et véhicules soignés : la régularité qui vaut à KS Rent Services une note de 5,0/5 sur 38 avis Google.",
      },
    ],
  },
  services: {
    heading: "Services chauffeur pendant la Fête du Citron",
    services: [
      {
        title: "Transfert aéroport & Monaco",
        text: "Nice ou Monaco vers Menton avec suivi de vol pendant les week-ends de corsos.",
      },
      {
        title: "Dépose aux jardins Biovès",
        text: "Approche au plus près de l'exposition d'agrumes et des tribunes selon les fermetures de rue.",
      },
      {
        title: "Accès aux corsos",
        text: "Dépose et reprise autour du front de mer pour les corsos des fruits d'or et les corsos de nuit.",
      },
      {
        title: "Mise à disposition journée",
        text: "Chauffeur dédié pour enchaîner jardins, vieille ville et déjeuner côté mer ou côté italien.",
      },
      {
        title: "Navettes familles & groupes",
        text: "Coordination multi-véhicules pour familles, groupes et invités.",
      },
    ],
  },
  locations: {
    heading: "Points clés pendant la Fête du Citron",
    items: [
      {
        name: "Jardins Biovès",
        description: "Sculptures géantes d'agrumes, cœur de la Fête du Citron.",
      },
      {
        name: "Promenade du Soleil",
        description: "Front de mer des corsos et défilés de chars.",
      },
      {
        name: "Vieille ville & port de Menton",
        description:
          "Ruelles, basilique et port pour le déjeuner ou la balade.",
      },
      {
        name: "Monaco",
        description:
          "À une dizaine de kilomètres, hôtels et gare pour les arrivées.",
      },
      {
        name: "Aéroport de Nice Côte d'Azur",
        description: "À une trentaine de kilomètres, terminaux 1 et 2.",
      },
    ],
  },
  generic: {
    heading: "La Fête du Citron vue d'un chauffeur",
    subheading:
      "Chaque hiver en février, Menton célèbre l'agrume avec ses sculptures des jardins Biovès et ses corsos sur le front de mer.",
    paragraphs: [
      "Chaque année en février, la Fête du Citron attire des centaines de milliers de visiteurs à Menton. Les jardins Biovès accueillent les sculptures d'agrumes, et les corsos défilent sur la Promenade du Soleil : les rues se ferment et le stationnement devient rare, surtout les week-ends.",
      "Menton est à une dizaine de kilomètres de Monaco et à une trentaine de l'aéroport de Nice Côte d'Azur. Avec le suivi de vol et l'accueil pancarte, le transfert vers votre hôtel se fait sans attente, même en pleine affluence.",
      "En hiver, enchaîner jardins, corsos et déjeuner sans marcher longtemps dans le froid change tout. La mise à disposition garde le même chauffeur toute la journée et vous dépose au plus près à chaque étape.",
    ],
    bullets: [
      "Dépose aux jardins Biovès",
      "Monaco à ~10 min",
      "Trajets au chaud en hiver",
    ],
  },
  process: {
    heading: "Réserver un chauffeur pour la Fête du Citron",
    steps: [
      "Indiquez vos dates, votre hôtel et les corsos que vous souhaitez voir.",
      "Nous calons les déposes selon les fermetures de rue autour des jardins Biovès.",
      "Le chauffeur se présente en tenue, avec suivi de vol pour vos arrivées à Nice.",
      "Transfert direct ou mise à disposition pour couvrir jardins, corsos et déjeuner.",
    ],
  },
  faq: [
    {
      q: "Comment réserver un chauffeur privé pendant la Fête du Citron ?",
      a: "Réservez à l'avance : en février, Menton se remplit et la demande grimpe les week-ends de corsos. Précisez vos dates, votre hôtel et les défilés que vous voulez voir pour que nous bloquions un chauffeur dédié.",
    },
    {
      q: "Où le chauffeur peut-il me déposer pour les jardins Biovès et les corsos ?",
      a: "Pendant les corsos, le front de mer et le centre de Menton sont fermés à la circulation. Le chauffeur vous dépose au point autorisé le plus proche des jardins Biovès ou des tribunes, puis vous reprend à la sortie.",
    },
    {
      q: "Gérez-vous les transferts depuis l'aéroport de Nice et Monaco ?",
      a: "Oui, avec suivi de vol et accueil pancarte. L'aéroport de Nice Côte d'Azur est à une trentaine de kilomètres et Monaco à une dizaine : nous adaptons l'heure de prise en charge en cas de retard.",
    },
    {
      q: "Le stationnement est-il compliqué pendant la fête ?",
      a: "Oui. Les jours de corsos, les rues se ferment et les parkings de Menton saturent. Le chauffeur vous évite de chercher une place : il dépose, patiente ou revient à l'heure convenue.",
    },
    {
      q: "La formule est-elle adaptée aux familles ?",
      a: "Tout à fait. En hiver, un chauffeur évite aux enfants et aux aînés l'attente dehors et les correspondances : le trajet se fait au chaud, au plus près des jardins et des corsos.",
    },
    {
      q: "Quels véhicules proposez-vous ?",
      a: "Berlines, SUV et vans avec chauffeur selon la taille de votre groupe, pour un trajet confortable entre l'hôtel, les jardins et le front de mer.",
    },
    {
      q: "Quand a lieu la Fête du Citron ?",
      a: "Elle se tient chaque année en février, sur environ trois semaines, avec des corsos plusieurs jours de suite. Réservez tôt pour les week-ends, les plus fréquentés.",
    },
  ],
  relatedLinks: [
    {
      label: "Réserver un chauffeur à Menton",
      href: "/chauffeur/menton",
    },
    {
      label: "Voiture avec chauffeur à Monaco",
      href: "/chauffeur/monaco",
    },
    {
      label: "Réserver un transfert aéroport",
      href: "/chauffeur/aeroport",
    },
    {
      label: "Mise à disposition avec chauffeur Côte d’Azur",
      href: "/chauffeur/cote-d-azur",
    },
  ],
  cta: {
    title: "Votre chauffeur pour la Fête du Citron",
    text: "Transfert aéroport, dépose aux jardins Biovès et accès aux corsos : réservez un chauffeur dédié pour votre visite à Menton.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver un chauffeur",
  },
  seo: {
    title: "Chauffeur privé Fête du Citron Menton | KS Rent",
    description:
      "Chauffeur privé pendant la Fête du Citron à Menton : transfert aéroport de Nice et Monaco, dépose aux jardins Biovès et accès aux corsos. Réservez.",
    canonical: "/chauffeur/fete-du-citron-menton",
  },
};

export default feteDuCitronMenton;
